import React, { memo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import Heading from '@atlaskit/heading';
import EditorCloseIcon from '@atlaskit/icon/core/migration/cross--editor-close';
import { token } from '@atlaskit/tokens';

import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { EPIC_CARD_DESCRIPTION_ID } from '../../../constants.tsx';
import { cardColor } from '../../card/constants.tsx';
import messages from './messages.tsx';
import type { HeaderProps } from './types.tsx';

const Header = ({ title, onClose, intl: { formatMessage } }: HeaderProps) => {
	const label = formatMessage(messages.closeButtonLabel, { title });

	return (
		<Container data-testid="software-backlog-panel.common.ui.panel.header.container">
			<TitleWrapper>
				<Title data-testid="software-backlog-panel.common.ui.panel.header.title">
					<Heading as="h2" size="xsmall">
						{title}
					</Heading>
				</Title>
				<ScreenReaderText id={EPIC_CARD_DESCRIPTION_ID}>
					{formatMessage(messages.epicCardDescription)}
				</ScreenReaderText>
			</TitleWrapper>
			<ButtonWrapper>
				<Button
					appearance="subtle"
					spacing="none"
					onClick={onClose}
					testId="software-backlog-panel.common.ui.panel.header.close-button"
				>
					<IconWrapper>
						<EditorCloseIcon spacing="spacious" label={label} color={token('color.icon')} />
					</IconWrapper>
				</Button>
			</ButtonWrapper>
		</Container>
	);
};

export { Header };

export default injectIntl(memo<HeaderProps>(Header));

const ICON_SIZE = gridSize * 3;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	flexShrink: 0,
	paddingTop: token('space.150'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWrapper = styled.div({
	display: 'inline-flex',
	flexGrow: 1,
	minWidth: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonWrapper = styled.div({
	display: 'inline-flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${ICON_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ICON_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${ICON_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minHeight: `${ICON_SIZE}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${ICON_SIZE}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${ICON_SIZE}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.div({
	display: 'block',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: cardColor.default.textColorSubtle,
	paddingLeft: token('space.100'),
});
